import React, { memo, useState } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Popup } from '@shared/ui';

import { PlanFactPositionItem } from './PlanFactPositionItem';
import cls from './PositionIItem.module.css';
import { useAppSelector } from '../../../../../store';
import { getFinalWidthPosition } from '../../../helpers/getFinalWidth';
import { getMinWidth } from '../../../hooks/getMinWidth';
import { useMovePosition } from '../../../hooks/useMovePosition';
import { useResizePosition } from '../../../hooks/useResizePosition';
import { ConnectionArrow } from '../ConnectionArrow/ConnectionArrow';
import { ItemMover } from '../ItemMover/ItemMover';
import { ItemResizer } from '../ItemResizer/ItemResizer';
import { PositionCircle } from '../PositionCircle/PositionCircle';

export const PositionIItem = memo(({ position, isPlanFact }) => {
    const { t } = useTranslation();
    const connection = useSelector((state) => state.connectionReducer);
    const { connections: connectionsList } = useAppSelector(
        (state) => state.positionsConnectionsReducer
    );
    const { chainPositionMove, activeTab } = useSelector((state) => state.gantReducer);
    const [isShow, setIsShow] = useState(false);
    const { positionX, handleMouseMove, handleMouseUp } = useMovePosition(position, activeTab);

    const connections = connectionsList.filter(
        (connectionItem) => connectionItem.from === position.guid
    );

    const { elWidth, changeWidth, onItemChange } = useResizePosition(
        activeTab,
        position,
        isPlanFact
    );

    const finalWidth = getFinalWidthPosition(elWidth, activeTab);

    if (!position) {
        return null;
    }

    const isActive = (positionCircle) =>
        connection.first.guid === `${position.guid}` &&
        positionCircle === connection.first.position;

    const isShowLeft = () => {
        if (!connection.isConnection) {
            return true;
        }
        if (isActive('left')) {
            return true;
        }
        if (connection.first.type === 0) {
            return true;
        }
    };
    const isShowRight = () => {
        if (!connection.isConnection) {
            return true;
        }
        if (isActive('right')) {
            return true;
        }
        if (connection.first.type === 1) {
            return true;
        }
    };

    return (
        <>
            <Popup
                contentText={t('Gant.PositionIItem.return.Popup.text')}
                onHidePopup={() => setIsShow(false)}
                isOpen={isShow}
                type="alert"
            />
            {isPlanFact ? (
                <PlanFactPositionItem position={position} positionX={positionX} />
            ) : (
                <div className={classNames(cls.wrapper)}>
                    {connections.map((connectionItem) => (
                        <ConnectionArrow
                            connection={connectionItem}
                            key={connectionItem.from + connectionItem.to + connectionItem.type}
                            positionX={positionX + chainPositionMove.positionX}
                        />
                    ))}
                    <div
                        data-position-guid={position.guid}
                        style={{ left: positionX + chainPositionMove.positionX }}
                        className={classNames(cls.StageItem, { [cls.planFact]: isPlanFact })}
                    >
                        <ItemMover onMove={handleMouseMove} onMouseUp={handleMouseUp}>
                            <div
                                title={position?.name}
                                data-for={`diagram-position-${position.guid}`}
                                style={{ width: finalWidth, minWidth: getMinWidth(activeTab) }}
                                className={classNames(cls.MainElement, [cls[position.type]], {
                                    [cls.backgroundFact]: isPlanFact,
                                    [cls.notPadding]:
                                        activeTab !== 'day' && +position.planned.days_count < 3,
                                })}
                            >
                                <span className={cls.text}>{position?.name}</span>

                                <ItemResizer
                                    onStageChange={onItemChange}
                                    onResize={changeWidth}
                                    elWidth={elWidth}
                                    classes={cls.resizer}
                                />
                            </div>
                        </ItemMover>
                        {isShowLeft() && (
                            <PositionCircle
                                setIsShow={setIsShow}
                                classes={[cls.connectionLeft, isActive('left') ? cls.active : '']}
                                position={position}
                                positionCircle="left"
                                widthPosition={elWidth}
                            />
                        )}
                        {isShowRight() && (
                            <PositionCircle
                                setIsShow={setIsShow}
                                classes={[cls.connectionRight, isActive('right') ? cls.active : '']}
                                position={position}
                                positionCircle="right"
                                widthPosition={elWidth}
                            />
                        )}
                    </div>
                </div>
            )}
        </>
    );
});
